/*
  CA3 product landing template: gallery, variant picker, add to cart, sticky bar and the small
  behaviours of the content sections. Loaded (deferred) by every CA3 section, so it guards itself.
  No dependencies. Talks to the theme only through the cart:refresh event.
*/
(() => {
  if (window.__ca3) return;
  window.__ca3 = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = window.matchMedia('(min-width: 900px)');
  const onFrame = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn(...args);
      });
    };
  };
  /* Translated strings and money output arrive HTML-escaped from Liquid;
     decode them once so textContent never shows a literal entity. */
  const decoder = document.createElement('textarea');
  const decode = (value) => {
    if (typeof value === 'string') {
      if (value.indexOf('&') === -1) return value;
      decoder.innerHTML = value;
      return decoder.value;
    }
    if (Array.isArray(value)) return value.map(decode);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach((key) => {
        value[key] = decode(value[key]);
      });
    }
    return value;
  };
  const readJSON = (root, selector, fallback) => {
    const el = root && root.querySelector(selector);
    if (!el) return fallback;
    try {
      return decode(JSON.parse(el.textContent));
    } catch (e) {
      return fallback;
    }
  };
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  /* ---------- Gallery: desktop mosaic, phone carousel, lightbox ---------- */
  class Ca3Gallery extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.items = Array.from(this.track.children);
      this.limit = parseInt(this.dataset.limit, 10) || 7;
      this.dots = this.querySelector('[data-dots]');
      this.count = this.querySelector('[data-count]');
      this.more = this.querySelector('[data-show-all]');
      this.moreLabel = this.querySelector('[data-show-all-label]');
      this.strings = readJSON(this.closest('ca3-product'), 'script[data-strings]', {});
      this.active = 0;
      // The lead picture fills the mosaic column and the others half of it. Keep both sizes values so
      // a picture that moves into or out of the lead downloads a file that suits its new box.
      const sizedImg = (li) => li.querySelector('img.ca3-gallery__img[sizes]');
      const leadItem = this.items.find((li) => li.classList.contains('is-lead') && sizedImg(li));
      const tileItem = this.items.find((li) => !li.classList.contains('is-lead') && sizedImg(li));
      this.sizesLead = leadItem ? sizedImg(leadItem).getAttribute('sizes') : '';
      this.sizesTile = tileItem ? sizedImg(tileItem).getAttribute('sizes') : '';

      this.track.addEventListener('scroll', onFrame(() => this.updateActive()), { passive: true });
      if (this.more) {
        this.more.setAttribute('aria-expanded', 'false');
        this.more.addEventListener('click', () => this.toggleAll());
      }
      this.track.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open]');
        if (!opener) return;
        const item = opener.closest('.ca3-gallery__item');
        this.openLightbox(item);
      });
      this.initLightbox();
      this.buildDots();
      this.updateActive();
    }

    visibleItems() {
      return this.items.filter((li) => !li.hidden);
    }

    setVariant(variant) {
      if (!variant || !this.track || !this.items.length) return;
      const probe = ',' + variant.id + ',';
      let visible = this.items.filter((li) => !li.dataset.variants || li.dataset.variants.indexOf(probe) !== -1);
      if (!visible.length) visible = this.items.slice();
      let lead = variant.media ? visible.find((li) => li.dataset.mediaId === String(variant.media)) : null;
      if (!lead) {
        lead = visible.slice().sort((a, b) => a.dataset.index - b.dataset.index)[0];
      }
      const rest = this.items.filter((li) => li !== lead).sort((a, b) => a.dataset.index - b.dataset.index);
      const ordered = [lead].concat(rest);
      const current = Array.from(this.track.children);
      if (ordered.some((li, i) => current[i] !== li)) {
        ordered.forEach((li) => this.track.appendChild(li));
      }
      this.items = ordered;
      let pos = 0;
      ordered.forEach((li) => {
        const on = visible.indexOf(li) !== -1;
        li.hidden = !on;
        li.classList.toggle('is-lead', li === lead);
        if (on) pos += 1;
        li.classList.toggle('is-extra', on && pos > this.limit);
        const img = li.querySelector('img.ca3-gallery__img');
        if (img && this.sizesLead && this.sizesTile) {
          const want = li === lead ? this.sizesLead : this.sizesTile;
          if (img.getAttribute('sizes') !== want) img.setAttribute('sizes', want);
        }
      });
      const leadImg = lead.querySelector('img');
      if (leadImg) leadImg.loading = 'eager';
      this.dataset.visible = String(visible.length);
      if (this.more) this.more.hidden = visible.length <= this.limit;
      this.updateMoreLabel();
      this.track.scrollLeft = 0;
      this.buildDots();
      this.updateActive();
    }

    toggleAll() {
      const expanded = this.classList.toggle('is-expanded');
      this.more.setAttribute('aria-expanded', String(expanded));
      this.updateMoreLabel();
      if (!expanded) {
        this.more.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    }

    updateMoreLabel() {
      if (!this.moreLabel) return;
      const expanded = this.classList.contains('is-expanded');
      const total = this.visibleItems().length;
      if (expanded && this.strings.showLess) {
        this.moreLabel.textContent = this.strings.showLess;
      } else if (this.strings.showAll) {
        this.moreLabel.textContent = this.strings.showAll.replace('{count}', total);
      }
    }

    buildDots() {
      if (!this.dots) return;
      const total = this.visibleItems().length;
      this.dots.textContent = '';
      if (total < 2 || total > 10) return;
      for (let i = 0; i < total; i += 1) {
        const dot = document.createElement('span');
        dot.className = 'ca3-gallery__dot';
        this.dots.appendChild(dot);
      }
    }

    updateActive() {
      const total = this.visibleItems().length;
      const width = this.track.clientWidth || 1;
      const index = Math.max(0, Math.min(total - 1, Math.round(this.track.scrollLeft / width)));
      this.active = index;
      if (this.count) this.count.textContent = index + 1 + ' / ' + total;
      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      }
    }

    initLightbox() {
      this.lb = this.querySelector('[data-lightbox]');
      if (!this.lb || typeof this.lb.showModal !== 'function') {
        this.lb = null;
        return;
      }
      this.lbImg = this.lb.querySelector('[data-lb-img]');
      this.lbCount = this.lb.querySelector('[data-lb-count]');
      this.lbPrev = this.lb.querySelector('[data-lb-prev]');
      this.lbNext = this.lb.querySelector('[data-lb-next]');
      const stage = this.lb.querySelector('.ca3-lightbox__stage');
      this.lb.querySelector('[data-lb-close]').addEventListener('click', () => this.lb.close());
      this.lbPrev.addEventListener('click', () => this.stepLightbox(-1));
      this.lbNext.addEventListener('click', () => this.stepLightbox(1));
      this.lb.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
        if (event.key === 'ArrowRight') this.stepLightbox(1);
      });
      if (stage) {
        stage.addEventListener('click', (event) => {
          if (event.target === stage) this.lb.close();
        });
        let startX = null;
        stage.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') startX = event.clientX;
        });
        stage.addEventListener('pointerup', (event) => {
          if (startX === null) return;
          const dx = event.clientX - startX;
          startX = null;
          if (Math.abs(dx) > 40) this.stepLightbox(dx < 0 ? 1 : -1);
        });
        stage.addEventListener('pointercancel', () => {
          startX = null;
        });
      }
      this.lbImg.addEventListener('load', () => this.lbImg.classList.remove('is-loading'));
    }

    lightboxItems() {
      return this.visibleItems().filter((li) => li.dataset.kind === 'image' && li.querySelector('img'));
    }

    openLightbox(item) {
      if (!this.lb) return;
      this.lbList = this.lightboxItems();
      const index = Math.max(0, this.lbList.indexOf(item));
      const single = this.lbList.length < 2;
      this.lbPrev.hidden = single;
      this.lbNext.hidden = single;
      this.showLightbox(index);
      this.lb.showModal();
    }

    stepLightbox(dir) {
      if (!this.lbList || this.lbList.length < 2) return;
      const n = this.lbList.length;
      this.showLightbox((this.lbIndex + dir + n) % n);
    }

    showLightbox(index) {
      this.lbIndex = index;
      const src = this.lbList[index].querySelector('img');
      this.lbImg.classList.add('is-loading');
      this.lbImg.removeAttribute('srcset');
      this.lbImg.width = Number(src.getAttribute('width')) || src.naturalWidth || 1200;
      this.lbImg.height = Number(src.getAttribute('height')) || src.naturalHeight || 1200;
      this.lbImg.sizes = '100vw';
      if (src.getAttribute('srcset')) this.lbImg.srcset = src.getAttribute('srcset');
      this.lbImg.src = src.currentSrc || src.src;
      this.lbImg.alt = src.alt || '';
      if (this.lbImg.complete) this.lbImg.classList.remove('is-loading');
      if (this.lbCount) this.lbCount.textContent = this.lbList.length > 1 ? index + 1 + ' / ' + this.lbList.length : '';
    }
  }

  /* ---------- Product: options, price, add to cart, sticky bar ---------- */
  class Ca3Product extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.variants = readJSON(this, 'script[data-variants]', []);
      this.strings = readJSON(this, 'script[data-strings]', {});
      this.form = this.querySelector('form[data-ca3-form]');
      if (!this.form || !this.variants.length) return;
      this.idInput = this.form.querySelector('[data-variant-id]');
      this.atc = this.form.querySelector('[data-atc]');
      this.msgEl = this.form.querySelector('[data-msg]');
      this.fieldsets = Array.from(this.querySelectorAll('fieldset[data-option]'));
      this.gallery = this.querySelector('ca3-gallery');
      this.buy = this.querySelector('[data-buy]');
      this.variant = this.variants.find((v) => String(v.id) === String(this.idInput.value)) || this.variants[0];

      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.classList || !input.classList.contains('ca3-opt__input')) return;
        const fieldset = input.closest('fieldset[data-option]');
        if (fieldset) this.onOption(parseInt(fieldset.dataset.option, 10));
      });
      this.initQty();
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      this.initSticky();
      this.initBuyTop();
      this.markAvailability();
      /* Browsers may restore radio states on reload; follow them rather than the server render. */
      const chosen = this.selectedOptions();
      if (this.variant && chosen.some((o, k) => o !== null && o !== this.variant.options[k])) this.onOption(0);
    }

    disconnectedCallback() {
      if (this.sticky && this.sticky.parentNode === document.body) this.sticky.remove();
      if (this.stickyCheck) {
        window.removeEventListener('scroll', this.stickyCheck);
        window.removeEventListener('resize', this.stickyCheck);
      }
    }

    selectedOptions() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('.ca3-opt__input:checked');
        return checked ? checked.value : null;
      });
    }

    onOption(index) {
      const chosen = this.selectedOptions();
      let variant = this.variants.find((v) => v.options.every((o, k) => o === chosen[k]));
      if (!variant) {
        const candidates = this.variants.filter((v) => v.options[index] === chosen[index]);
        variant = candidates.find((v) => v.available) || candidates[0];
      }
      if (variant) this.setVariant(variant);
    }

    valueAvailable(index, value) {
      const current = this.variant ? this.variant.options : this.selectedOptions();
      return this.variants.some(
        (v) => v.available && v.options[index] === value && v.options.every((o, k) => k === index || o === current[k])
      );
    }

    markAvailability() {
      this.fieldsets.forEach((fs) => {
        const index = parseInt(fs.dataset.option, 10);
        fs.querySelectorAll('.ca3-opt__input').forEach((input) => {
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !this.valueAvailable(index, input.value));
        });
      });
    }

    setVariant(variant) {
      this.variant = variant;
      this.fieldsets.forEach((fs) => {
        const index = parseInt(fs.dataset.option, 10);
        const value = variant.options[index];
        fs.querySelectorAll('.ca3-opt__input').forEach((input) => {
          input.checked = input.value === value;
        });
        const current = fs.querySelector('[data-option-current]');
        if (current) current.textContent = value;
      });

      this.idInput.value = variant.id;
      this.idInput.setAttribute('value', variant.id);
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));

      const now = this.querySelector('[data-price-now]');
      const was = this.querySelector('[data-price-was]');
      if (now) now.textContent = variant.price;
      if (was) {
        was.hidden = !variant.compare;
        was.textContent = variant.compare || '';
      }

      const label = variant.available ? this.strings.addToCart : this.strings.soldOut;
      this.atc.disabled = !variant.available;
      const atcLabel = this.atc.querySelector('[data-atc-label]');
      const atcPrice = this.atc.querySelector('[data-atc-price]');
      const atcSep = this.atc.querySelector('.ca3-atc__sep');
      if (atcLabel && label) atcLabel.textContent = label;
      if (atcPrice) {
        atcPrice.textContent = variant.price;
        atcPrice.hidden = !variant.available;
      }
      if (atcSep) atcSep.hidden = !variant.available;

      const stock = this.querySelector('[data-stock-row]');
      if (stock) stock.hidden = !variant.stock;
      const dispatch = this.querySelector('[data-dispatch]');
      if (dispatch) dispatch.hidden = !variant.available;

      this.markAvailability();
      this.updateSticky();
      if (this.gallery && this.gallery.setVariant) this.gallery.setVariant(variant);
      this.setMessage('');

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) {
        /* URL API unavailable: keep the address as is. */
      }

      this.dispatchEvent(new CustomEvent('ca3:variant', { bubbles: true, detail: { variant, product: this } }));
    }

    initQty() {
      this.qtyInput = this.form.querySelector('[data-qty-input]');
      if (!this.qtyInput) return;
      this.qtyButtons = Array.from(this.form.querySelectorAll('[data-qty]'));
      const max = parseInt(this.qtyInput.max, 10) || 99;
      const clamp = () => {
        let n = parseInt(this.qtyInput.value, 10);
        if (!Number.isFinite(n) || n < 1) n = 1;
        if (n > max) n = max;
        this.qtyInput.value = n;
        this.qtyButtons.forEach((b) => {
          const step = parseInt(b.dataset.qty, 10);
          b.disabled = step < 0 ? n <= 1 : n >= max;
        });
      };
      this.qtyButtons.forEach((b) =>
        b.addEventListener('click', () => {
          this.qtyInput.value = (parseInt(this.qtyInput.value, 10) || 1) + parseInt(b.dataset.qty, 10);
          clamp();
        })
      );
      this.qtyInput.addEventListener('change', clamp);
      clamp();
    }

    setMessage(text, isError) {
      if (!this.msgEl) return;
      clearTimeout(this.msgTimer);
      this.msgEl.textContent = text || '';
      this.msgEl.classList.toggle('is-error', !!isError);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.atc.disabled) return;
      this.busy = true;
      this.atc.classList.add('is-busy');
      this.atc.setAttribute('aria-busy', 'true');
      this.setMessage('');
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let ok = false;
      try {
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || this.strings.error);
        ok = true;
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        this.setMessage(this.strings.added);
        this.msgTimer = setTimeout(() => this.setMessage(''), 5000);
      } catch (error) {
        this.setMessage((error && error.message) || this.strings.error, true);
      } finally {
        this.busy = false;
        this.atc.classList.remove('is-busy');
        this.atc.removeAttribute('aria-busy');
        this.flashSticky(ok ? this.strings.added : this.strings.error);
      }
    }

    /* Sticky bar: appears once the main add-to-cart button has scrolled away above the screen. */
    initSticky() {
      const bar = this.querySelector('.ca3-sticky');
      if (!bar || !this.atc) return;
      this.sticky = bar;
      this.stickyBtn = bar.querySelector('[data-sticky-atc]');
      this.stickyPrice = bar.querySelector('[data-sticky-price]');
      this.stickyVariant = bar.querySelector('[data-sticky-variant]');
      this.stickyThumb = bar.querySelector('[data-sticky-thumb] img');
      /* Any transformed ancestor would pin the bar to that ancestor instead of the screen. */
      bar.classList.add('ca3');
      document.body.appendChild(bar);
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => {
          if (this.atc.disabled || this.busy) return;
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(this.atc);
          else this.atc.click();
        });
      }
      const show = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
        if (this.stickyBtn) this.stickyBtn.tabIndex = visible ? 0 : -1;
      };
      /* Measured on scroll as well: an instant jump from below the button to the top of a phone
         page (where the button starts below the screen) never crosses it, so an observer alone
         would leave the bar showing. */
      const check = onFrame(() => show(this.atc.getBoundingClientRect().bottom < 0));
      this.stickyCheck = check;
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check, { passive: true });
      check();
    }

    updateSticky() {
      if (!this.sticky) return;
      const v = this.variant;
      if (this.stickyPrice) this.stickyPrice.textContent = v.price;
      if (this.stickyVariant && this.variants.length > 1) this.stickyVariant.textContent = v.title;
      if (this.stickyBtn) {
        this.stickyBtn.disabled = !v.available;
        this.stickyBtn.textContent = v.available ? this.strings.addToCart : this.strings.soldOut;
      }
      if (this.stickyThumb && v.media) {
        const img = this.querySelector('.ca3-gallery__item[data-media-id="' + v.media + '"] img');
        if (img) {
          if (img.getAttribute('srcset')) this.stickyThumb.srcset = img.getAttribute('srcset');
          this.stickyThumb.src = img.getAttribute('src');
        }
      }
    }

    flashSticky(text) {
      if (!this.stickyBtn || !text || !this.sticky.classList.contains('is-visible')) return;
      clearTimeout(this.flashTimer);
      this.stickyBtn.textContent = text;
      this.flashTimer = setTimeout(() => this.updateSticky(), 2400);
    }

    /* Desktop: a buy box taller than the screen sticks by its bottom edge instead of its top. */
    initBuyTop() {
      if (!this.buy) return;
      const gap = 24;
      const update = () => {
        if (!desktop.matches) {
          this.buy.style.removeProperty('--ca3-buy-top');
          return;
        }
        const vh = window.innerHeight;
        const h = this.buy.offsetHeight;
        this.buy.style.setProperty('--ca3-buy-top', (h + gap * 2 <= vh ? gap : Math.round(vh - h - gap)) + 'px');
      };
      const schedule = onFrame(update);
      if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(this.buy);
      window.addEventListener('resize', schedule, { passive: true });
      if (desktop.addEventListener) desktop.addEventListener('change', schedule);
      update();
    }
  }

  /* ---------- Horizontal scrollers with previous / next buttons ---------- */
  class Ca3Scroller extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.track = this.querySelector('[data-scroll-track]');
      if (!this.track) return;
      const id = this.track.id;
      if (id) {
        this.prev = document.querySelector('[data-scroll-prev][aria-controls="' + id + '"]');
        this.next = document.querySelector('[data-scroll-next][aria-controls="' + id + '"]');
      }
      this.nav = this.prev ? this.prev.closest('[data-scroll-nav]') : null;
      const update = onFrame(() => this.update());
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(this.track);
      new MutationObserver(update).observe(this.track, { childList: true });
      this.update();
    }

    go(dir) {
      const t = this.track;
      t.scrollBy({ left: dir * Math.max(t.clientWidth * 0.8, 240), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const t = this.track;
      const overflow = t.scrollWidth > t.clientWidth + 4;
      if (this.nav) this.nav.hidden = !overflow;
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 2;
    }
  }

  /* ---------- Tissue fit: typical stacks, the visitor's own stack, plan to scale ---------- */
  // Same rules as sections/ca3-fit.liquid, in whole millimetres, long side against long side:
  // longer than the space, or wider by more than 5 mm, won't fit; taller: take out at least N% of
  // the tissues (rounded up to a ten; above 50%, won't fit); wider by up to 5 mm: tight.
  const FIT_SLACK = 5;
  const fitVerdict = (stack, max) => {
    const L = Math.max(stack.l, stack.w);
    const W = Math.min(stack.l, stack.w);
    const H = stack.h || 0;
    const mL = Math.max(max.l, max.w);
    const mW = Math.min(max.l, max.w);
    const mH = max.h || 0;
    if (L > mL) return { key: 'no', text: 'long', diff: L - mL };
    if (W > mW + FIT_SLACK) return { key: 'no', text: 'wide', diff: W - mW };
    if (H > 0 && mH > 0 && H > mH) {
      const pct = Math.ceil(((H - mH) * 10) / H) * 10;
      return pct > 50 ? { key: 'no', text: 'tall' } : { key: 'trim', text: 'cut', pct };
    }
    if (W > mW) return { key: 'tight', text: 'squeeze', diff: W - mW };
    if (!mH) return { key: 'ok', text: 'ok_noh' };
    if (!H) return { key: 'ok', text: 'ok_base', diff: mH };
    return { key: 'ok', text: '' };
  };
  const setRect = (el, x, y, w, h, rx) => {
    if (!el) return;
    const on = w > 0 && h > 0;
    el.classList.toggle('is-off', !on);
    if (!on) return;
    el.setAttribute('x', x);
    el.setAttribute('y', y);
    el.setAttribute('width', w);
    el.setAttribute('height', h);
    if (rx !== undefined) el.setAttribute('rx', rx);
  };

  class Ca3Fit extends HTMLElement {
    connectedCallback() {
      if (this._ready) {
        if (this.onVariant) document.addEventListener('ca3:variant', this.onVariant);
        return;
      }
      this._ready = true;
      this.data = readJSON(this, 'script[data-fit-json]', null);
      if (!this.data || !this.data.variants) return;
      this.strings = this.data.strings || {};
      this.lang = this.data.lang || 'en';
      this.plan = this.querySelector('[data-fit-plan]');
      this.result = this.querySelector('[data-fit-result]');
      this.figure = this.plan ? this.plan.closest('figure') : null;
      this.inputs = {};
      this.querySelectorAll('[data-fit-in]').forEach((el) => {
        this.inputs[el.dataset.fitIn] = el;
      });
      this.parts = {};
      if (this.plan) {
        this.planG = this.plan.querySelector('[data-g="plan"]');
        this.sideG = this.plan.querySelector('[data-g="side"]');
        this.plan.querySelectorAll('[data-p]').forEach((el) => {
          this.parts['p-' + el.dataset.p] = el;
        });
        this.plan.querySelectorAll('[data-s]').forEach((el) => {
          this.parts['s-' + el.dataset.s] = el;
        });
      }
      /* The browser may restore the unit or typed values on reload. */
      const unit = this.querySelector('[data-fit-unit-choice]:checked');
      this.unit = (unit ? unit.value : this.data.unit) === 'in' ? 'in' : 'cm';
      this.syncSuffix();

      this.initTabs();
      this.addEventListener('input', (event) => {
        if (!event.target.matches || !event.target.matches('[data-fit-in]')) return;
        this.markPack(null);
        this.update();
      });
      this.addEventListener('change', (event) => {
        const t = event.target;
        if (t.matches && t.matches('[data-fit-unit-choice]') && t.checked) this.setUnit(t.value);
      });
      this.addEventListener('click', (event) => {
        const btn = event.target.closest && event.target.closest('[data-fit-use]');
        if (btn) this.usePack(btn.closest('[data-pack]'));
      });
      this.onVariant = (event) => {
        const v = event.detail && event.detail.variant;
        if (!v) return;
        this.setLimits(v.id);
        this.verdictRows();
        this.update();
      };
      document.addEventListener('ca3:variant', this.onVariant);

      /* The product form may already have switched variants before this element woke up. */
      const product = document.querySelector('ca3-product');
      const id = product && product.variant ? product.variant.id : this.data.current;
      this.setLimits(id);
      if (String(id) !== String(this.data.current)) this.verdictRows();
      this.update();
    }

    disconnectedCallback() {
      if (this.onVariant) document.removeEventListener('ca3:variant', this.onVariant);
    }

    setLimits(id) {
      const v = this.data.variants[String(id)] || null;
      this.box = v;
      this.limits = v && v.l > 0 && v.w > 0 ? v : null;
    }

    fmtNum(value) {
      const text = String(Math.round(value * 10) / 10);
      return this.lang === 'en' ? text : text.replace('.', ',');
    }

    /* German and French use cm only. In English the result for typed sizes leads with the unit the
       visitor measures in; the rows for common boxes keep the page's order (inches first), so each
       row's verdict reads the same way as the sizes printed above it. */
    fmtLen(mm) {
      const cm = `${this.fmtNum(mm / 10)} cm`;
      if (this.lang !== 'en') return cm;
      const inch = `${this.fmtNum(mm / 25.4)} in`;
      return this.unit === 'in' || this.rowOrder ? `${inch} (${cm})` : `${cm} (${inch})`;
    }

    message(v) {
      const s = this.strings;
      switch (v.text) {
        case 'long':
          return (s.long || '').replace('%S%', this.fmtLen(v.diff));
        case 'wide':
          return (s.wide || '').replace('%S%', this.fmtLen(v.diff));
        case 'tall':
          return s.tall || '';
        case 'cut':
          return (s.cut || '').replace('%P%', String(v.pct));
        case 'squeeze':
          return (s.squeeze || '').replace('%S%', this.fmtLen(v.diff));
        case 'ok_noh':
          return s.ok_noh || '';
        case 'ok_base':
          return (s.ok_base || '').replace('%S%', this.fmtLen(v.diff));
        default:
          return '';
      }
    }

    /* Fills a verdict: a label ("Too tall"), a hidden ": " for screen readers, then the detail. */
    paint(el, v) {
      if (!el) return;
      el.className = el.className.replace(/\s*ca3-fit__verdict--\w+/g, '') + ' ca3-fit__verdict--' + v.key;
      const label = el.querySelector('[data-v-label]');
      const sep = el.querySelector('[data-v-sep]');
      const text = el.querySelector('[data-v-text]');
      const msg = this.message(v);
      if (label) label.textContent = this.strings[v.key] || '';
      if (text) {
        text.textContent = msg;
        text.hidden = !msg;
      }
      if (sep) sep.hidden = !msg;
      el.hidden = false;
    }

    verdictRows() {
      this.rowOrder = true;
      this.querySelectorAll('[data-pack]').forEach((row) => {
        const el = row.querySelector('[data-v]');
        if (!el) return;
        if (!this.limits) {
          el.hidden = true;
          return;
        }
        const stack = { l: +row.dataset.l || 0, w: +row.dataset.w || 0, h: +row.dataset.h || 0 };
        this.paint(el, fitVerdict(stack, this.limits));
      });
      this.rowOrder = false;
    }

    toMm(raw) {
      const n = parseFloat(String(raw || '').replace(',', '.').trim());
      if (!Number.isFinite(n) || n <= 0) return 0;
      return this.unit === 'in' ? Math.round(n * 25.4) : Math.round(n * 10);
    }

    fromMm(mm) {
      if (!mm) return '';
      const v = this.unit === 'in' ? mm / 25.4 : mm / 10;
      return this.fmtNum(v);
    }

    readStack() {
      const get = (k) => (this.inputs[k] ? this.toMm(this.inputs[k].value) : 0);
      const l = get('l');
      const w = get('w');
      if (!l || !w) return null;
      return { l, w, h: get('h') };
    }

    syncSuffix() {
      this.querySelectorAll('[data-fit-suffix]').forEach((el) => {
        el.textContent = this.unit;
      });
    }

    setUnit(unit) {
      const next = unit === 'in' ? 'in' : 'cm';
      if (next === this.unit) return;
      const mm = {};
      Object.keys(this.inputs).forEach((k) => {
        mm[k] = this.toMm(this.inputs[k].value);
      });
      this.unit = next;
      Object.keys(this.inputs).forEach((k) => {
        if (mm[k]) this.inputs[k].value = this.fromMm(mm[k]);
      });
      this.syncSuffix();
      this.verdictRows();
      this.update();
    }

    markPack(row) {
      this.querySelectorAll('[data-pack].is-active').forEach((el) => {
        if (el !== row) el.classList.remove('is-active');
      });
      if (row) row.classList.add('is-active');
    }

    usePack(row) {
      if (!row) return;
      const values = { l: +row.dataset.l || 0, w: +row.dataset.w || 0, h: +row.dataset.h || 0 };
      Object.keys(this.inputs).forEach((k) => {
        this.inputs[k].value = this.fromMm(values[k]);
      });
      this.markPack(row);
      this.update();
      const target = this.figure || this.result;
      if (!target) return;
      const box = target.getBoundingClientRect();
      if (box.top < 72 || box.bottom > window.innerHeight) {
        target.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    }

    update() {
      const stack = this.readStack();
      let v = null;
      if (this.result) {
        const s = this.strings;
        this.result.textContent = '';
        this.result.className = 'ca3-fit__result';
        if (!this.limits) {
          this.result.textContent = s.unmeasured || '';
        } else if (!stack) {
          this.result.textContent = s.empty || '';
        } else {
          v = fitVerdict(stack, this.limits);
          this.result.className = 'ca3-fit__result ca3-fit__verdict ca3-fit__verdict--' + v.key;
          const label = document.createElement('span');
          label.className = 'ca3-fit__v-label';
          label.textContent = s[v.key] || '';
          this.result.append(label);
          const msg = this.message(v);
          if (msg) {
            const sep = document.createElement('span');
            sep.className = 'ca3-vh';
            sep.textContent = ': ';
            const text = document.createElement('span');
            text.className = 'ca3-fit__v-text';
            text.textContent = msg;
            this.result.append(sep, text);
          }
        }
      }
      this.layout(this.limits ? stack : null, v ? v.key : 'ok');
    }

    /* Redraws the plan in millimetres: from above on the left, from the side on the right. */
    layout(stack, key) {
      if (!this.plan || !this.planG) return;
      const box = this.box || {};
      const lim = this.limits || { l: 0, w: 0, h: 0 };
      const mL = Math.max(lim.l, lim.w);
      const mW = Math.min(lim.l, lim.w);
      const mH = lim.h || 0;
      let cL = Math.max(box.cl || 0, box.cw || 0);
      let cW = Math.min(box.cl || 0, box.cw || 0);
      if (!cW) cL = 0;
      const cH = box.ch || 0;
      const sL = stack ? Math.max(stack.l, stack.w) : 0;
      const sW = stack ? Math.min(stack.l, stack.w) : 0;
      const sH = stack ? stack.h || 0 : 0;
      const pad = 12;
      const gap = 36;
      const PL = Math.max(mL, cL, sL);
      const PW = Math.max(mW, cW, sW);
      const GH = Math.max(mH, cH, sH);
      const HH = Math.max(PW, GH);
      const side = GH > 0;
      if (!PL || !HH) return;
      const vbW = pad * 2 + PL + (side ? gap + PW : 0);
      this.plan.setAttribute('viewBox', `0 0 ${vbW} ${pad * 2 + HH}`);
      this.planG.setAttribute('transform', `translate(${pad + PL / 2} ${pad + HH / 2})`);
      const p = this.parts;
      setRect(p['p-cover'], -cL / 2, -cW / 2, cL, cW, Math.round(cW * 1.2) / 10);
      setRect(p['p-max'], -mL / 2, -mW / 2, mL, mW, 2);
      const stackClass = 'ca3-fit__p-stack ca3-fit__p-stack--' + key;
      ['p-stack', 's-stack'].forEach((k) => {
        if (p[k]) p[k].setAttribute('class', stackClass);
      });
      setRect(p['p-stack'], -sL / 2, -sW / 2, sL, sW, 2);
      if (this.sideG) {
        this.sideG.classList.toggle('is-off', !side);
        this.sideG.setAttribute('transform', `translate(${pad + PL + gap + PW / 2} ${pad + HH / 2 + GH / 2})`);
        setRect(p['s-cover'], -cW / 2, -cH, cW, cH, 2);
        setRect(p['s-max'], -mW / 2, -mH, mW, mH, 0);
        setRect(p['s-stack'], -sW / 2, -sH, sW, sH, 1);
        if (p['s-ground']) p['s-ground'].setAttribute('d', `M${-(PW / 2 + 8)} 0 H${PW / 2 + 8}`);
      }
      const legend = this.figure;
      if (legend) {
        const keyStack = legend.querySelector('[data-key="stack"]');
        const keyCover = legend.querySelector('[data-key="cover"]');
        const both = legend.querySelector('[data-views="both"]');
        const top = legend.querySelector('[data-views="top"]');
        if (keyStack) keyStack.hidden = !(sL && sW);
        if (keyCover) keyCover.hidden = !cL;
        if (both) both.hidden = !side;
        if (top) top.hidden = side;
      }
    }

    initTabs() {
      const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (event) => {
          let j = -1;
          if (event.key === 'ArrowRight') j = (i + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') j = 0;
          else if (event.key === 'End') j = tabs.length - 1;
          if (j < 0) return;
          event.preventDefault();
          select(tabs[j], true);
        });
      });
    }
  }

  define('ca3-gallery', Ca3Gallery);
  define('ca3-product', Ca3Product);
  define('ca3-scroller', Ca3Scroller);
  define('ca3-fit', Ca3Fit);

  /* ---------- Variant-aware sections ---------- */
  document.addEventListener('ca3:variant', (event) => {
    const { variant } = event.detail;
    const probe = ',' + variant.id + ',';

    document.querySelectorAll('[data-variant-sets]').forEach((wrap) => {
      const sets = Array.from(wrap.children).filter((el) => el.hasAttribute('data-for-variants'));
      if (!sets.length) return;
      const hit = sets.find((s) => s.dataset.forVariants.indexOf(probe) !== -1) || sets[0];
      sets.forEach((s) => {
        s.hidden = s !== hit;
      });
    });
  });

  document.addEventListener('click', (event) => {
    /* The universal template's body also has the ca3 class (template suffix), so skip it. */
    if (!event.target.closest || !event.target.closest('.ca3:not(body)')) return;
    const panelBtn = event.target.closest('[data-open-panel]');
    if (panelBtn) {
      const scope = panelBtn.closest('ca3-product') || document;
      const key = panelBtn.getAttribute('data-open-panel');
      const panel = Array.from(scope.querySelectorAll('details.ca3-panel[data-panel]')).find((d) => d.dataset.panel === key);
      if (!panel) return;
      panel.open = true;
      const top = panel.getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight - 96) {
        panel.scrollIntoView({ block: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      const summary = panel.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
      return;
    }

    const faqLink = event.target.closest('[data-open-faq]');
    if (faqLink) {
      const key = faqLink.getAttribute('data-open-faq');
      const item = Array.from(document.querySelectorAll('details[data-faq-key]')).find((d) => d.dataset.faqKey === key);
      if (!item) return;
      event.preventDefault();
      item.open = true;
      item.scrollIntoView({ block: 'start', behavior: reduceMotion() ? 'auto' : 'smooth' });
      const summary = item.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
      return;
    }

    const more = event.target.closest('[data-more]');
    if (more) {
      const body = document.getElementById(more.getAttribute('aria-controls'));
      if (body) body.classList.add('is-open');
      more.setAttribute('aria-expanded', 'true');
      more.hidden = true;
    }
  });

  /* ---------- Review cards: show "Read more" only when the text is actually cut ---------- */
  const checkClamps = () => {
    document.querySelectorAll('[data-clamp]').forEach((body) => {
      if (body.classList.contains('is-open')) return;
      const btn = body.parentElement && body.parentElement.querySelector('[data-more][aria-controls="' + body.id + '"]');
      if (btn) btn.hidden = body.scrollHeight <= body.clientHeight + 2;
    });
  };
  const scheduleClamps = onFrame(checkClamps);
  checkClamps();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleClamps);
  window.addEventListener('resize', scheduleClamps, { passive: true });
  window.addEventListener('load', scheduleClamps);

  /* ---------- Related products: fetched from Shopify recommendations when no collection is set ---------- */
  const loadRelated = async (block) => {
    try {
      const response = await fetch(block.dataset.relatedUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('[data-related-list]');
      const target = block.querySelector('[data-related-list]');
      if (!source || !target || !source.querySelector('li')) return;
      target.innerHTML = source.innerHTML;
      block.hidden = false;
    } catch (e) {
      /* Leave the section hidden. */
    }
  };
  const initRelated = () => {
    document.querySelectorAll('[data-related-url]').forEach((block) => {
      if (block._ca3Related) return;
      block._ca3Related = true;
      const anchor = block.parentElement || block;
      if (!('IntersectionObserver' in window)) {
        loadRelated(block);
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          loadRelated(block);
        },
        { rootMargin: '900px 0px' }
      );
      io.observe(anchor);
    });
  };
  initRelated();

  /* ---------- Judge.me review dates: same format as the store review cards ---------- */
  // Judge.me prints its app-wide format (mm/dd/yyyy) but keeps the UTC time in data-content, so the
  // text is rewritten to the UTC calendar date in the store cards' format: "Jul 21, 2026",
  // "21.07.2026" or "21/07/2026". Re-applied whenever Judge.me re-renders the list (paging).
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const reviewDate = (value, lang) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
    if (!m || !MONTHS[Number(m[2]) - 1]) return null;
    if (lang === 'de') return `${m[3]}.${m[2]}.${m[1]}`;
    if (lang === 'fr') return `${m[3]}/${m[2]}/${m[1]}`;
    return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
  };
  const fixReviewDates = (root) => {
    const lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    root.querySelectorAll('.jdgm-rev__timestamp[data-content]').forEach((el) => {
      const text = reviewDate(el.getAttribute('data-content'), lang);
      if (text && el.textContent !== text) el.textContent = text;
    });
  };
  const initReviewDates = () => {
    document.querySelectorAll('.ca3-jdgm').forEach((root) => {
      if (root._ca3Dates) return;
      root._ca3Dates = true;
      fixReviewDates(root);
      if (!('MutationObserver' in window)) return;
      const refresh = onFrame(() => fixReviewDates(root));
      new MutationObserver(refresh).observe(root, { childList: true, subtree: true, characterData: true });
    });
  };
  initReviewDates();

  /* Theme editor: sections are re-rendered in place. */
  document.addEventListener('shopify:section:load', () => {
    initRelated();
    initReviewDates();
    scheduleClamps();
  });
})();
